/**
 * React Query mutation hooks for the code-review (paused_for_review)
 * flow. All four mutations land on the SAME backing document
 * (analysis_item.codes_quotes_dict) so they share an invalidation set
 * — separated into one helper to keep each hook focused on its call
 * signature.
 *
 * Every hook takes ``analysisItemId`` at construction time so the
 * mutation payloads pin the write to a specific analysis_item. Without
 * this, customer-api falls back to "the latest analysis_item in the
 * study's array" — wrong the moment a study has more than one
 * Grounded Emergence run.
 *
 * Cache keys invalidated on every mutation:
 *   ['interview-analysis-code', studyId]  — codes_quotes_dict feed
 *   ['interview-codebook', studyId]       — categories feed
 *   ['codebook', studyId, analysisItemId] — per-analysis categories
 *   ['analysisDetail', studyId, analysisItemId] — analysis-item detail
 *   ['interview-analysis', studyId, analysisItemId] — themes
 *   ['overall_findings', studyId, analysisItemId] — report
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  assignCodeToTurn,
  createCodeOnTurn,
  deleteCode,
  renameCode,
  unassignCodeFromTurn,
} from '@services/codeReview';
import { showErrorToast } from '@utils/toast';

function invalidateCodeReviewQueries(queryClient, studyId, analysisItemId) {
  queryClient.invalidateQueries({ queryKey: ['interview-analysis-code', studyId] });
  queryClient.invalidateQueries({ queryKey: ['interview-codebook', studyId] });
  queryClient.invalidateQueries({ queryKey: ['codebook', studyId] });
  queryClient.invalidateQueries({ queryKey: ['analysisDetail', studyId] });
  queryClient.invalidateQueries({ queryKey: ['interview-analysis', studyId] });
  queryClient.invalidateQueries({ queryKey: ['overall_findings', studyId] });
  // Specific-item keys live under the same prefixes; the invalidate
  // calls above already cover them. analysisItemId is accepted so a
  // future refinement can scope the invalidation more tightly.
  void analysisItemId;
}

export function useRenameCode(studyId, analysisItemId = null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ oldCode, newCode, definition }) =>
      renameCode({ studyId, analysisItemId, oldCode, newCode, definition }),
    onSuccess: () => invalidateCodeReviewQueries(queryClient, studyId, analysisItemId),
    onError: (error) => showErrorToast(error),
  });
}

export function useDeleteCode(studyId, analysisItemId = null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code }) => deleteCode({ studyId, analysisItemId, code }),
    onSuccess: () => invalidateCodeReviewQueries(queryClient, studyId, analysisItemId),
    onError: (error) => showErrorToast(error),
  });
}

export function useUnassignCodeFromTurn(studyId, analysisItemId = null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ qualitativeItemId, code, turnId }) =>
      unassignCodeFromTurn({ studyId, analysisItemId, qualitativeItemId, code, turnId }),
    onSuccess: () => invalidateCodeReviewQueries(queryClient, studyId, analysisItemId),
    onError: (error) => showErrorToast(error),
  });
}

export function useCreateCodeOnTurn(studyId, analysisItemId = null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ qualitativeItemId, codeName, turnId, definition, highlightedText }) =>
      createCodeOnTurn({
        studyId,
        analysisItemId,
        qualitativeItemId,
        codeName,
        turnId,
        definition,
        highlightedText,
      }),
    onSuccess: () => invalidateCodeReviewQueries(queryClient, studyId, analysisItemId),
    onError: (error) => showErrorToast(error),
  });
}

export function useAssignCodeToTurn(studyId, analysisItemId = null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ qualitativeItemId, codeName, turnId, highlightedText }) =>
      assignCodeToTurn({
        studyId,
        analysisItemId,
        qualitativeItemId,
        codeName,
        turnId,
        highlightedText,
      }),
    onSuccess: () => invalidateCodeReviewQueries(queryClient, studyId, analysisItemId),
    onError: (error) => showErrorToast(error),
  });
}
